import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAdmin, isStaff } from '../utils/userRoleHelpers';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredContext?: 'admin' | 'staff' | 'client';
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredContext 
}) => {
  const { isAuthenticated, isLoading, session, user } = useAuth();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    const loginPath = requiredContext ? `/login/${requiredContext}` : '/login/client';
    return <Navigate to={loginPath} replace />;
  }

  // If we have a session and user, check context access
  if (requiredContext && session && user) {
    // Simple role-based access control
    const hasContextAccess = (() => {
      switch (requiredContext) {
        case 'admin':
          return isAdmin(user);
        case 'staff':
          return isStaff(user);
        case 'client':
          return true; // All authenticated users can access client context
        default:
          return false;
      }
    })();
    
    // If user doesn't have access to this context, redirect to appropriate login
    if (!hasContextAccess) {
      const loginPath = `/login/${requiredContext}`;
      return <Navigate to={loginPath} replace />;
    }
  }

  return <>{children}</>;
};